import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';

export default function Settings() {
  const [settings, setSettings] = useState<Record<string, string>>({
    defaultCategory: '',
    defaultCurrency: 'SGD',
    gstRate: '9',
    imageQuality: '90',
    autoSlug: 'true',
    apiEndpoint: '/api',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const result = await api.settings.get();
        setSettings((prev) => ({ ...prev, ...result }));
      } catch {
        // Use defaults
      }
    };
    fetchSettings();
  }, []);

  const updateSetting = useCallback((key: string, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await api.settings.update(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [settings]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Settings</h2>
          <p className="text-muted-foreground">Configure uploader preferences</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="text-sm text-success">Settings saved!</span>
          )}
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-foreground">General</h3>
          <div className="space-y-4">
            <div>
              <label className="label-field">API Endpoint</label>
              <input
                type="text"
                value={settings.apiEndpoint}
                onChange={(e) => updateSetting('apiEndpoint', e.target.value)}
                className="input-field mt-1"
              />
            </div>
            <div>
              <label className="label-field">Default Category</label>
              <select
                value={settings.defaultCategory}
                onChange={(e) => updateSetting('defaultCategory', e.target.value)}
                className="input-field mt-1"
              >
                <option value="">None</option>
                <option value="smartphones">Smartphones</option>
                <option value="laptops">Laptops</option>
                <option value="tablets">Tablets</option>
                <option value="audio">Audio</option>
                <option value="wearables">Wearables</option>
                <option value="accessories">Accessories</option>
                <option value="gaming">Gaming</option>
                <option value="smart-home">Smart Home</option>
              </select>
            </div>
            <div>
              <label className="label-field">Currency</label>
              <input
                type="text"
                value={settings.defaultCurrency}
                onChange={(e) => updateSetting('defaultCurrency', e.target.value)}
                className="input-field mt-1"
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-foreground">Pricing</h3>
          <div className="space-y-4">
            <div>
              <label className="label-field">GST Rate (%)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={settings.gstRate}
                onChange={(e) => updateSetting('gstRate', e.target.value)}
                className="input-field mt-1"
              />
            </div>
            <div>
              <label className="label-field">Default Image Quality (%)</label>
              <input
                type="number"
                min="1"
                max="100"
                value={settings.imageQuality}
                onChange={(e) => updateSetting('imageQuality', e.target.value)}
                className="input-field mt-1"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={settings.autoSlug === 'true'}
                onChange={(e) => updateSetting('autoSlug', e.target.checked ? 'true' : 'false')}
                className="h-4 w-4 rounded border-border text-primary-600 focus:ring-primary-600"
              />
              <label className="label-field">Auto-generate slugs from product name</label>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-foreground">Media Upload</h3>
          <div className="space-y-4">
            <div>
              <label className="label-field">Max Image Size (MB)</label>
              <input
                type="number"
                min="1"
                max="20"
                value="5"
                className="input-field mt-1"
                disabled
              />
              <p className="mt-1 text-2xs text-muted-foreground">Server-side limit</p>
            </div>
            <div>
              <label className="label-field">Max Video Size (MB)</label>
              <input
                type="number"
                min="10"
                max="500"
                value="100"
                className="input-field mt-1"
                disabled
              />
              <p className="mt-1 text-2xs text-muted-foreground">Server-side limit</p>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-foreground">Danger Zone</h3>
          <div className="space-y-4">
            <div className="rounded-lg border border-destructive/30 p-4">
              <h4 className="text-sm font-medium text-destructive">Clear All Drafts</h4>
              <p className="mt-1 text-xs text-muted-foreground">
                This will permanently delete all saved drafts.
              </p>
              <button
                type="button"
                onClick={() => {
                  if (confirm('Are you sure? This cannot be undone.')) {
                    localStorage.removeItem('sggadgets_uploader_drafts');
                  }
                }}
                className="btn-destructive mt-3"
              >
                Clear Drafts
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
